import { createCodec } from './datapointCodec';
import { STARTER_TYPES } from './constants';
import { newProgramme } from './workspace';
import { exerciseFromVariant, variantIndex } from '../data/catalogue';
import type { AgeTier, Catalogue, Exercise, Programme, StarterType } from '../types';

/**
 * Packs a Programme's figure ids into a short, URL-safe fragment —
 * `<version>:<#athletes>:<agegroup>:<data>`, e.g. "1:2:e:<data>" for a v1
 * pair/eliteJunior programme — so it can be shared as a link (WhatsApp etc.)
 * and auto-imported on open. Deliberately excludes the programme's name and
 * any hand-edited figure text: both are re-derived from the catalogue by
 * figure id on import, the same "derive, don't duplicate" choice already
 * made for club/athlete facts elsewhere in this codebase.
 */
export const SHARE_VERSION = 1;

/**
 * One codec instance for every StarterType (team6 included, via team4's id
 * space — see the CODEC_PREFIX table below). The largest real stripped
 * figure value across all three catalogues is 473 (pair), comfortably under
 * the default maxValue of 999, so no per-type tuning is needed.
 *
 * `continuation: true` escapes any double-letter suffix (the pair catalogue
 * currently has 14, e.g. "2070aa") via one extra slot rather than a fixed
 * table, so catalogue growth can never silently break old links. `stamp:
 * true` costs one character but turns an accidental future change to this
 * configuration into a clean decode error instead of a silently misread
 * share link — worth it since these links are meant to be posted into a
 * messenger and can sit unopened for a long time. `maxDatapoints: 30`
 * matches the domain's hard cap on figures per programme.
 */
const idCodec = createCodec({
  minValue: 1, maxValue: 605, digits: 3,
  continuation: true, stamp: true, maxDatapoints: 30,
});

/** The codec's numeric prefix for a type's figure ids. Team 6 has no figure
 *  data of its own and borrows Team 4's catalogue/id-space (see CLAUDE.md's
 *  Team 6 limitation), so it is not its own digit here. */
const CODEC_PREFIX: Readonly<Record<StarterType, number>> = {
  single: 1,
  pair: 2,
  team4: 4,
  team6: 4,
};

const RIDERS_TO_TYPE: ReadonlyMap<number, StarterType> = new Map(
  (Object.keys(STARTER_TYPES) as StarterType[]).map((type) => [STARTER_TYPES[type].riders, type]),
);

const AGE_TIER_CHAR: Readonly<Record<AgeTier, string>> = { eliteJunior: 'e', youth: 'y' };
const CHAR_TO_AGE_TIER: ReadonlyMap<string, AgeTier> = new Map(
  (Object.entries(AGE_TIER_CHAR) as Array<[AgeTier, string]>).map(([tier, char]) => [char, tier]),
);

export class ShareFormatError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ShareFormatError';
  }
}

export interface DecodedShare {
  type: StarterType;
  ageTier: AgeTier;
  /** Full figure ids (e.g. "2277c"), ready to resolve against a catalogue. */
  ids: string[];
}

const SHARE_PATTERN = /^(\d+):(\d+):([a-z]):(.*)$/;

/** Cheap shape check, so a caller can ignore any hash that isn't a share
 *  link (e.g. this app's own view-switch hashes like "#sportler") without
 *  having to catch an exception for the common case. */
export function looksLikeProgrammeShare(fragment: string): boolean {
  return SHARE_PATTERN.test(fragment);
}

/** Builds the `<version>:<#athletes>:<agegroup>:<data>` fragment for a
 *  Programme. Does not include the leading "#" — callers append that. */
export function encodeProgrammeShare(programme: Programme): string {
  const riders = STARTER_TYPES[programme.type].riders;
  const ageTierChar = AGE_TIER_CHAR[programme.ageTier];
  const ids = programme.program.map((exercise) => exercise.nr);
  const data = idCodec.encode(ids, { prefix: CODEC_PREFIX[programme.type] });
  return `${SHARE_VERSION}:${riders}:${ageTierChar}:${data}`;
}

/** Parses a share fragment back into a type/age-tier/figure-id list. Throws
 *  `ShareFormatError` for anything envelope-level (bad version, unknown
 *  athlete count or age-tier letter) and `DatapointCodecError` (from
 *  `datapointCodec.ts`) for a corrupt data segment — callers that just want
 *  "was this a valid share link" can catch `Error` generically. */
export function decodeProgrammeIds(fragment: string): DecodedShare {
  const match = SHARE_PATTERN.exec(fragment);
  if (!match) {
    throw new ShareFormatError(`Not a recognisable share link: ${JSON.stringify(fragment)}.`);
  }
  const [, versionStr, ridersStr, ageTierChar, data] = match;
  const version = Number(versionStr);
  if (version !== SHARE_VERSION) {
    throw new ShareFormatError(`Unsupported share link version ${version}.`);
  }
  const type = RIDERS_TO_TYPE.get(Number(ridersStr));
  if (type === undefined) {
    throw new ShareFormatError(`Unknown athlete count ${ridersStr} in share link.`);
  }
  const ageTier = CHAR_TO_AGE_TIER.get(ageTierChar!);
  if (ageTier === undefined) {
    throw new ShareFormatError(`Unknown age tier ${JSON.stringify(ageTierChar)} in share link.`);
  }
  const ids = idCodec.decode(data!, CODEC_PREFIX[type]);
  return { type, ageTier, ids };
}

export interface BuiltSharedProgramme {
  programme: Programme;
  /** Decoded ids not found in the catalogue — skipped, never fabricated,
   *  mirroring `buildWeboProgramme()`'s `unmatchedNrs` in `importWebo.ts`. */
  skippedIds: readonly string[];
}

/**
 * Resolves a decoded share's figure ids against an already-loaded catalogue,
 * the same `Exercise` a coach would get by picking the figure by hand
 * (`exerciseFromVariant()`, also used by `FigurePicker.tsx` and the WeBo
 * importer). Pure and synchronous — the caller fetches the catalogue
 * (`loadCatalogue(catalogueKeyFor(type))`) first, matching
 * `buildWeboProgramme()`'s split between resolution and fetching.
 */
export function buildSharedProgramme(decoded: DecodedShare, catalogue: Catalogue): BuiltSharedProgramme {
  const index = variantIndex(catalogue);
  const program: Exercise[] = [];
  const skippedIds: string[] = [];
  for (const id of decoded.ids) {
    const variant = index.get(id);
    if (variant) program.push(exerciseFromVariant(variant));
    else skippedIds.push(id);
  }
  const programme: Programme = { ...newProgramme(decoded.type, decoded.ageTier), program };
  return { programme, skippedIds };
}
